import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { LogOut, Sun, Moon, User, ChevronDown, Bell } from 'lucide-react';

const Header = () => {
  const { user, adminUser, logout, isAdmin } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [showLogoutMenu, setShowLogoutMenu] = useState(false);
  const [showUpdatesModal, setShowUpdatesModal] = useState(false);
  const [hasNewUpdates, setHasNewUpdates] = useState(false);
  const logoutMenuRef = useRef(null);

  // Verificar se há atualizações não vistas
  useEffect(() => {
    const currentVersion = '2.8';
    const lastSeenVersion = localStorage.getItem('stakemind_last_seen_version');
    setHasNewUpdates(lastSeenVersion !== currentVersion);
  }, []);

  // Fechar menu quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (logoutMenuRef.current && !logoutMenuRef.current.contains(event.target)) {
        setShowLogoutMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = (clearRememberMe = false) => {
    logout(clearRememberMe);
    setShowLogoutMenu(false);
  };

  const handleOpenUpdates = () => {
    setShowUpdatesModal(true);
    setHasNewUpdates(false);
  };

  const handleCloseUpdates = () => {
    setShowUpdatesModal(false);
    const currentVersion = '2.8';
    localStorage.setItem('stakemind_last_seen_version', currentVersion);
  };

  const currentUser = adminUser || user;

  return (
    <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 backdrop-blur-sm bg-opacity-90 dark:bg-opacity-90">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Title */}
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-green-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <h1 className="text-base sm:text-xl font-bold bg-gradient-to-r from-teal-400 to-green-400 bg-clip-text text-transparent">
                <span translate="no">Stake Mind</span>
              </h1>
            </div>
          </div>

          {/* User Info & Actions */}
          <div className="flex items-center space-x-4">
            {/* Notifications Icon */}
            <button
              onClick={handleOpenUpdates}
              className="relative p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title="Novidades e atualizações"
            >
              <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              {hasNewUpdates && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              )}
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title={isDarkMode ? 'Modo claro' : 'Modo escuro'}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 text-gray-600" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>

            {/* User Info */}
            {currentUser && (
              <div className="flex items-center space-x-2 text-sm">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-gray-700 dark:text-gray-300">
                  {currentUser.name}
                </span>
              </div>
            )}

            {/* Logout Menu */}
            <div className="relative" ref={logoutMenuRef}>
              <button
                onClick={() => setShowLogoutMenu(!showLogoutMenu)}
                className="p-2 rounded-lg bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors text-red-600 dark:text-red-400 flex items-center space-x-1"
                title="Opções de logout"
              >
                <LogOut className="w-5 h-5" />
                <ChevronDown className="w-3 h-3" />
              </button>

              {showLogoutMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="py-2">
                    <button
                      onClick={() => handleLogout(false)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sair (manter login salvo)</span>
                    </button>
                    <button
                      onClick={() => handleLogout(true)}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sair e esquecer dados</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Atualizações */}
      {showUpdatesModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center pt-4 sm:pt-20 px-4">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full border-2 border-blue-200 dark:border-blue-800 animate-slide-down overflow-hidden">
            {/* Header com gradiente */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 opacity-20">
                <Bell className="w-32 h-32" />
              </div>
              <button
                onClick={handleCloseUpdates}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors z-10"
                aria-label="Fechar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="flex items-center space-x-3 mb-2 relative z-10">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Bell className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">🎯 NOVIDADES STAKE MIND</h2>
                  <p className="text-blue-100">Versão 2.8 - Melhorias Importantes</p>
                </div>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              
              {/* Filtros de Período em Grupos */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                      📊 FILTROS DE PERÍODO EM GRUPOS
                    </h3>
                    <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      <li>✅ Agora você pode filtrar seus grupos por período!</li>
                      <li>• Mês Atual / Mês Anterior</li>
                      <li>• Últimos 3 Meses / Todo o Período</li>
                      <li>• Período Personalizado</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Suporte WhatsApp */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-l-4 border-green-500 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                      📱 SUPORTE SEMPRE À MÃO
                    </h3>
                    <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      <li>✅ Botão de WhatsApp no menu lateral</li>
                      <li>✅ Número de suporte na página inicial</li>
                      <li>• Acesso rápido: (11) 91656-0595</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Melhorias de Performance */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-l-4 border-purple-500 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3">
                  <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                      🔧 MELHORIAS DE PERFORMANCE
                    </h3>
                    <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      <li>✅ Login mais rápido e estável</li>
                      <li>✅ Interface mais responsiva</li>
                      <li>✅ Correções de bugs críticos</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Footer inspiracional */}
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl p-4 text-white text-center">
                <p className="font-semibold text-lg">
                  ✨ Versão 2.8 Disponível Agora!
                </p>
                <p className="text-sm text-blue-100 mt-1">
                  Continue profissionalizando suas apostas com o Stake Mind!
                </p>
              </div>
            </div>

            {/* Botão de ação */}
            <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCloseUpdates}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg"
              >
                Entendi! Vamos explorar 🚀
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;